import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, BrainCircuit, CheckCircle2, FileText } from "lucide-react";

// Dir 143 — the UI half of the Dir-137 backend extraction. Calls POST /library/documents/{id}/ingest-brain
// (Claude proposes structured Past-Performance + Key-Personnel records, facts-only, nothing fabricated),
// shows them for REVIEW (every field editable, blanks stay blank, include/exclude per row, provenance shown),
// and on Confirm saves via POST /brain/ingest/apply. NEVER auto-applies — the confirm click is the gate.
export default function IngestBrainModal({ doc, onClose, onApplied }) {
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [note, setNote] = useState("");
  const [pp, setPp] = useState([]);
  const [kp, setKp] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.post(`/library/documents/${doc.id}/ingest-brain`);
        if (!alive) return;
        setPp((r.data.past_performance || []).map((x) => ({ ...x, _include: true })));
        setKp((r.data.key_personnel || []).map((x) => ({ ...x, _include: true })));
        setNote(r.data.note || "");
      } catch (e) {
        if (alive) toast.error(e?.response?.data?.detail || "Extraction failed.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [doc.id]);

  const setPpField = (i, k, v) => setPp((rows) => rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const setKpField = (i, k, v) => setKp((rows) => rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));

  const strip = (r) => { const { _include, _source, ...rest } = r; return rest; };

  const apply = async () => {
    const ppSel = pp.filter((r) => r._include && (r.project_name || "").trim()).map(strip);
    const kpSel = kp.filter((r) => r._include && (r.full_name || "").trim()).map(strip);
    if (ppSel.length === 0 && kpSel.length === 0) return toast.error("Nothing selected to save.");
    setApplying(true);
    try {
      const r = await api.post("/brain/ingest/apply", {
        source_doc_id: doc.id, source_doc_name: doc.name,
        past_performance: ppSel, key_personnel: kpSel,
      });
      const s = r.data.saved || {};
      const added = (s.past_performance || 0) + (s.key_personnel || 0);
      toast.success(`Saved to Brain — ${added} new${s.updated ? `, ${s.updated} updated` : ""}. Check the Past Performance / Key Personnel tabs.`);
      onApplied && onApplied();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setApplying(false);
    }
  };

  const total = pp.length + kp.length;

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/50" onClick={() => !applying && onClose()}>
      <div className="w-full max-w-2xl bg-white border border-zinc-200 rounded-xl shadow-2xl max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="ingest-brain-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200">
          <div className="flex items-center gap-2 min-w-0">
            <span className="h-8 w-8 rounded-full bg-gold-dark/15 flex items-center justify-center shrink-0"><BrainCircuit className="h-5 w-5 text-gold-dark" /></span>
            <div className="min-w-0">
              <div className="font-display font-bold text-zinc-900">Extract to Brain</div>
              <div className="text-[11px] text-zinc-500 flex items-center gap-1 truncate"><FileText className="h-3 w-3 shrink-0" /> from {doc.name}</div>
            </div>
          </div>
          <button onClick={() => !applying && onClose()} data-testid="ingest-close"><X className="h-5 w-5 text-zinc-400 hover:text-zinc-900" /></button>
        </div>

        <div className="p-5 overflow-y-auto space-y-5">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-zinc-500 py-8" data-testid="ingest-loading"><Loader2 className="h-4 w-4 animate-spin" /> Anvil is reading the document and extracting records…</div>
          ) : total === 0 ? (
            <div className="text-sm text-zinc-500 py-8 text-center border border-dashed border-zinc-300 rounded-lg" data-testid="ingest-empty">
              {note || "No past-performance or key-personnel records found in this document."}
            </div>
          ) : (
            <>
              <p className="text-[12px] text-zinc-500">Review the records Anvil extracted from this document. Edit any field, uncheck to skip, then Confirm. Nothing is saved until you confirm — blank fields are never guessed.</p>

              {pp.length > 0 && (
                <div data-testid="ingest-pp-section">
                  <div className="text-[12px] font-semibold text-zinc-700 mb-2">Past Performance ({pp.length})</div>
                  <div className="space-y-3">
                    {pp.map((r, i) => (
                      <div key={i} data-testid={`ingest-pp-${i}`} className={`border rounded-lg p-3 ${r._include ? "border-zinc-200" : "border-zinc-100 opacity-50"}`}>
                        <label className="flex items-center gap-2 mb-2 cursor-pointer">
                          <input type="checkbox" checked={r._include} onChange={(e) => setPpField(i, "_include", e.target.checked)} data-testid={`ingest-pp-include-${i}`} className="h-4 w-4" />
                          <span className="text-[11px] uppercase tracking-wider text-zinc-500">Include this reference</span>
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          <MiniField label="Project name" value={r.project_name} onChange={(v) => setPpField(i, "project_name", v)} testid={`ingest-pp-project-${i}`} full />
                          <MiniField label="Client" value={r.client} onChange={(v) => setPpField(i, "client", v)} testid={`ingest-pp-client-${i}`} />
                          <MiniField label="Contract #" value={r.contract_number} onChange={(v) => setPpField(i, "contract_number", v)} testid={`ingest-pp-contract-${i}`} />
                          <MiniField label="Value" value={r.value} onChange={(v) => setPpField(i, "value", v)} testid={`ingest-pp-value-${i}`} />
                          <MiniField label="Period" value={r.period} onChange={(v) => setPpField(i, "period", v)} testid={`ingest-pp-period-${i}`} />
                          <MiniField label="POC name" value={r.poc_name} onChange={(v) => setPpField(i, "poc_name", v)} testid={`ingest-pp-pocname-${i}`} />
                          <MiniField label="POC email (external)" value={r.poc_email} onChange={(v) => setPpField(i, "poc_email", v)} testid={`ingest-pp-pocemail-${i}`} />
                          <MiniField label="Scope" value={r.scope} onChange={(v) => setPpField(i, "scope", v)} testid={`ingest-pp-scope-${i}`} full />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {kp.length > 0 && (
                <div data-testid="ingest-kp-section">
                  <div className="text-[12px] font-semibold text-zinc-700 mb-2">Key Personnel ({kp.length})</div>
                  <div className="space-y-3">
                    {kp.map((r, i) => (
                      <div key={i} data-testid={`ingest-kp-${i}`} className={`border rounded-lg p-3 ${r._include ? "border-zinc-200" : "border-zinc-100 opacity-50"}`}>
                        <label className="flex items-center gap-2 mb-2 cursor-pointer">
                          <input type="checkbox" checked={r._include} onChange={(e) => setKpField(i, "_include", e.target.checked)} data-testid={`ingest-kp-include-${i}`} className="h-4 w-4" />
                          <span className="text-[11px] uppercase tracking-wider text-zinc-500">Include this person</span>
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          <MiniField label="Full name" value={r.full_name} onChange={(v) => setKpField(i, "full_name", v)} testid={`ingest-kp-name-${i}`} />
                          <MiniField label="Role" value={r.role} onChange={(v) => setKpField(i, "role", v)} testid={`ingest-kp-role-${i}`} />
                          <MiniField label="Years experience" value={r.years_experience} onChange={(v) => setKpField(i, "years_experience", v)} testid={`ingest-kp-years-${i}`} />
                          <MiniField label="Education" value={r.education} onChange={(v) => setKpField(i, "education", v)} testid={`ingest-kp-edu-${i}`} />
                          <MiniField label="Highlights" value={r.highlights} onChange={(v) => setKpField(i, "highlights", v)} testid={`ingest-kp-highlights-${i}`} full />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-zinc-200">
          <button onClick={() => !applying && onClose()} data-testid="ingest-cancel" className="text-sm px-4 py-2 rounded-md bg-zinc-100 hover:bg-zinc-200 text-zinc-700 disabled:opacity-50" disabled={applying}>Cancel</button>
          <button onClick={apply} disabled={loading || applying || total === 0} data-testid="ingest-confirm"
            className="flex items-center gap-2 text-sm px-4 py-2 rounded-md bg-gold-dark text-white hover:opacity-90 disabled:opacity-40">
            {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Confirm &amp; save to Brain
          </button>
        </div>
      </div>
    </div>
  );
}

function MiniField({ label, value, onChange, testid, full }) {
  return (
    <label className={`flex flex-col gap-1 text-[11px] text-zinc-500 ${full ? "col-span-2" : ""}`}>
      {label}
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="text-[12px] border border-zinc-300 rounded px-2 py-1.5 text-zinc-800" />
    </label>
  );
}
